import type {
  AnchorHTMLAttributes,
  ComponentPropsWithoutRef,
  ElementType,
  ReactNode,
  Ref,
} from 'react'

import { cn } from '@gto/ui/cn'
import { Spinner } from '@gto/ui/icons/spinner'
import type { Tone } from '@gto/ui/tone'

import { buttonVariants } from './button.styles'

type ButtonColor = 'primary' | 'secondary' | 'error' | 'warning' | 'info'
type ButtonVariant = 'contained' | 'outlined' | 'text'
type ButtonSize = 'small' | 'medium' | 'large'

/** Base props shared by all Button variants */
type BaseButtonProps = {
  /** Button color variant */
  color?: ButtonColor
  /** Tints from the caller's --accent-rgb, --accent-solid, --accent-fill-a and --accent-hover-fill-a, with green fallbacks. */
  accent?: 'custom'
  /** Button style variant */
  variant?: ButtonVariant
  /** Button size */
  size?: ButtonSize
  /** Surface the button sits on — `onPrimary` for a primary-green fill. */
  tone?: Tone
  /** Whether the button is disabled */
  disabled?: boolean
  /** Whether the button takes full width */
  fullWidth?: boolean
  /** Icon displayed before the label */
  startIcon?: ReactNode
  /** Icon displayed after the label */
  endIcon?: ReactNode
  /** Whether the button is in loading state */
  loading?: boolean
  /** Button content */
  children?: ReactNode
  /** Additional CSS class names */
  className?: string
  /** React 19 ref prop */
  ref?: Ref<HTMLElement>
  /** Test attribute for E2E testing */
  'data-tst'?: string
}

/** Props when explicitly using `as` prop for polymorphism */
type PolymorphicButtonProps<T extends ElementType> = BaseButtonProps & {
  /** The element type to render as */
  as?: T
  /** @deprecated Use `as` prop instead */
  component?: T
} & Omit<ComponentPropsWithoutRef<T>, keyof BaseButtonProps | 'as' | 'component'>

/** Props when using href (auto-detected as anchor) */
type AnchorButtonProps = BaseButtonProps & {
  /** URL to navigate to (automatically renders as anchor) */
  href: string
  as?: never
  component?: never
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof BaseButtonProps | 'href'>

/** Props for default button element */
type NativeButtonProps = BaseButtonProps & {
  href?: never
  as?: never
  component?: never
} & Omit<ComponentPropsWithoutRef<'button'>, keyof BaseButtonProps>

/** Union type for all Button prop variants */
type ButtonProps<T extends ElementType = 'button'> =
  | AnchorButtonProps
  | NativeButtonProps
  | PolymorphicButtonProps<T>

/**
 * Button component with Tailwind CSS styling
 *
 * Supports polymorphism via the `as` prop to render as different elements.
 * Automatically renders as an anchor (`<a>`) when `href` is provided.
 *
 * @example
 * // Basic usage
 * <Button onClick={handleClick}>Click me</Button>
 *
 * @example
 * // As a link (auto-detected from href)
 * <Button href="/path">Go to page</Button>
 *
 * @example
 * // With loading state
 * <Button loading>Saving...</Button>
 *
 * @example
 * // With icons
 * <Button startIcon={<PlusIcon />} endIcon={<ArrowIcon />}>Add item</Button>
 */
const Button = <T extends ElementType = 'button'>(props: ButtonProps<T>) => {
  const {
    as,
    // oxlint-disable-next-line typescript/no-deprecated
    component,
    color = 'primary',
    accent,
    variant = 'text',
    tone = 'default',
    size = 'medium',
    disabled = false,
    fullWidth = false,
    startIcon,
    endIcon,
    loading = false,
    children,
    className,
    ref,
    type,
    'data-tst': dataTestId,
    ...rest
  } = props as PolymorphicButtonProps<T> & { type?: string }
  // Auto-detect anchor when href is provided, fall back to 'component' for backward compatibility
  const Component = as ?? component ?? ('href' in rest ? 'a' : 'button')

  const isDisabled = disabled || loading

  const styles = buttonVariants({
    variant,
    tone,
    color,
    accent,
    size,
    fullWidth,
    disabled: isDisabled,
    loading,
  })

  // Determine button type attribute for native buttons
  const buttonType = Component === 'button' ? (type ?? 'button') : undefined

  return (
    <Component
      ref={ref as Ref<never>}
      aria-disabled={Component !== 'button' && isDisabled ? true : undefined}
      className={cn(styles.root(), className)}
      data-tst={dataTestId}
      data-ui="button"
      disabled={Component === 'button' ? isDisabled : undefined}
      type={buttonType}
      {...rest}
    >
      {loading && (
        <span
          className={styles.spinnerWrapper()}
          data-tst={dataTestId != null && dataTestId !== '' ? `${dataTestId}-spinner` : undefined}
        >
          <Spinner />
        </span>
      )}
      {Boolean(startIcon) && <span className={styles.iconWrapper()}>{startIcon}</span>}
      {children}
      {Boolean(endIcon) && <span className={styles.iconWrapper()}>{endIcon}</span>}
    </Component>
  )
}

export { Button }
export type { BaseButtonProps, ButtonColor, ButtonProps, ButtonSize, ButtonVariant }
